import type { ApiError, ApiErrorCode } from "@9amhealth/openapi";
import reportErrorSentry from "src/lib/reportErrorSentry";

/**
 * Stands in for "the response carried no error code". Callers treat the
 * formatted code as an opaque, always-truthy string — both as translation
 * context and as the signal that a request failed at all — so it must not
 * become empty when the code is missing.
 */
export const NO_ERROR_CODE = "0";

export const extractErrorCode = (error: unknown): ApiErrorCode | undefined => {
  try {
    const code = (error as ApiError | undefined)?.body?.code;
    if (code !== undefined && code !== null) {
      return Number(code) as ApiErrorCode;
    }
  } catch (e: unknown) {
    reportErrorSentry(e);
  }

  return undefined;
};

export const extractErrorCodeString = (error: unknown): string => {
  const code = extractErrorCode(error);
  return code === undefined ? NO_ERROR_CODE : `${code}`;
};

/**
 * HTTP status of a failed request, read from the top-level `status` the
 * generated client's `ApiError` carries. Timeouts, network failures and
 * non-HTTP errors have none and yield `undefined`.
 */
export const getErrorHttpStatus = (error: unknown): number | undefined => {
  if (typeof error !== "object" || error === null) {
    return undefined;
  }

  const { status } = error as { status?: unknown };
  return typeof status === "number" ? status : undefined;
};

const NETWORK_ERROR_MESSAGES = new Set([
  "Load failed",
  "Failed to fetch",
  "NetworkError when attempting to fetch resource."
]);

/** `fetch` rejects with a bare `TypeError` when the request never got a response, e.g. a dropped connection. */
export const isNetworkError = (error: unknown): boolean =>
  error instanceof TypeError && NETWORK_ERROR_MESSAGES.has(error.message);
